import { Button, Label, Slider } from '@design-system';
import React from 'react';
import { Control, Controller } from 'react-hook-form';

import { X } from 'lucide-react';

import { cn } from '@/lib';
import { isEqual } from 'lodash';

interface CustomRangeSliderProps {
  name: string;
  label?: React.ReactNode | ((value: number[]) => React.ReactNode);
  control: Control<any>;
  size?: 'sm' | 'default';
  testId?: string;
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: number[];
  showTooltip?: boolean;
  tooltipContent?: (value: number) => React.ReactNode;
  onValueChange?: (value: number[]) => void;
  headerActions?: React.ReactNode;
  headerLayout?: 'inline' | 'stacked';
  commitOnRelease?: boolean;
  snapToStepFromZero?: boolean;
  valueScale?: 'linear' | 'square-root';
}

const SCALED_SLIDER_STEPS = 10_000;

export function CustomRangeSlider({
  name,
  control,
  label,
  size = 'default',
  testId,
  min = 0,
  max = 100,
  step,
  defaultValue,
  onValueChange,
  headerActions,
  headerLayout = 'inline',
  commitOnRelease = false,
  snapToStepFromZero = false,
  valueScale = 'linear',
  ...props
}: CustomRangeSliderProps) {
  const [dragValue, setDragValue] = React.useState<number[] | null>(null);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => {
        const committedValue = Array.isArray(value) ? value : (defaultValue ?? [min, max]);
        const valueArray = dragValue ?? committedValue;
        const isDefault = isEqual(valueArray, defaultValue);
        const resolvedLabel = typeof label === 'function' ? label(valueArray) : label;
        const usesScaledTrack = valueScale === 'square-root' && max > min;

        const toSliderValue = (actualValue: number) => {
          if (!usesScaledTrack) return actualValue;
          const ratio = Math.max(0, Math.min(1, (actualValue - min) / (max - min)));
          return Math.sqrt(ratio) * SCALED_SLIDER_STEPS;
        };

        const fromSliderValue = (sliderValue: number) => {
          if (!usesScaledTrack) return sliderValue;
          const ratio = Math.max(0, Math.min(1, sliderValue / SCALED_SLIDER_STEPS));
          return min + ratio * ratio * (max - min);
        };

        const normalizeValues = (values: number[]) => {
          const actualValues = values.map(fromSliderValue);
          if (!snapToStepFromZero || !step) return actualValues;
          return actualValues.map(candidate => {
            if (candidate <= min) return min;
            if (candidate >= max) return max;
            return Math.max(min, Math.min(max, Math.round(candidate / step) * step));
          });
        };

        const resetButton = (
          <Button
            type="button"
            size={size === 'sm' ? 'icon' : 'sm'}
            variant={size === 'sm' ? 'ghost' : 'outline'}
            onClick={() => {
              setDragValue(null);
              onChange(defaultValue);
            }}
            aria-hidden={isDefault}
            tabIndex={isDefault ? -1 : undefined}
            className={cn('h-7 shrink-0 px-2 text-xs', {
              'size-5 p-0': size === 'sm',
              'pointer-events-none invisible': isDefault && headerLayout === 'inline',
              'hidden': isDefault && headerLayout === 'stacked',
            })}
          >
            {size === 'sm' ? <X className="size-3.5" /> : 'Reset'}
          </Button>
        );

        return (
          <div className="space-y-4">
            {/* No flex-wrap: in the narrow AOB/COB columns a long label such as
                "5 Years - Lifetime" would otherwise push the reset control onto its own
                line. The label shrinks and wraps instead, keeping the control in place. */}
            {headerLayout === 'stacked' ? (
              <div className="space-y-3">
                {resolvedLabel && (
                  <Label className="text-primary-dark block whitespace-nowrap text-lg font-semibold">
                    {resolvedLabel}
                  </Label>
                )}
                <div className="flex items-center gap-2">
                  {headerActions}
                  {resetButton}
                </div>
              </div>
            ) : (
              <div
                className={cn('flex items-center justify-between gap-4', {
                  'gap-2': size === 'sm',
                })}
              >
                {resolvedLabel && (
                  <Label
                    className={cn('text-primary-dark min-w-0 text-lg font-semibold', {
                      'text-xs': size === 'sm',
                    })}
                  >
                    {resolvedLabel}
                  </Label>
                )}
                {/* Kept in the tree at the default value, only hidden. A column whose filter is
                    untouched then reserves the same header height as one showing the control,
                    so side-by-side AOB/COB sliders stay on the same baseline. */}
                <div className="ml-auto flex items-center gap-2">
                  {headerActions}
                  {resetButton}
                </div>
              </div>
            )}
            <Slider
              data-testid={testId}
              value={valueArray.map(toSliderValue)}
              min={usesScaledTrack ? 0 : min}
              max={usesScaledTrack ? SCALED_SLIDER_STEPS : max}
              step={usesScaledTrack || snapToStepFromZero ? 1 : step}
              onValueChange={newValue => {
                const normalized = normalizeValues(newValue);
                if (commitOnRelease) {
                  setDragValue(normalized);
                  return;
                }
                onValueChange?.(normalized);
                onChange(normalized);
              }}
              onValueCommit={newValue => {
                if (!commitOnRelease) return;
                const normalized = normalizeValues(newValue);
                onValueChange?.(normalized);
                onChange(normalized);
                setDragValue(null);
              }}
              onPointerUp={() => {
                if (commitOnRelease) setDragValue(null);
              }}
              className="**:data-[slot=slider-thumb]:size-4 **:data-[slot=slider-track]:h-1.5"
            />
          </div>
        );
      }}
    />
  );
}
